import { ClipboardList, Eye, ShoppingCart, Wrench } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import type { MantenimientoRow } from '../api';
import { EstadoConSaldoEcheq, saldoEnDespacho, useSaldosEcheq } from '@/shared/components/EcheqSaldoTag';

/**
 * Gallery for post-payment ventas, laid out to match the PowerApps
 * Screen_Mantenimiento design: Status · N° Cotización · F. Venta ·
 * Vendedor · Cliente · Teléfono · Correo · Acciones.
 *
 * Each row has three action icons:
 *   - Clipboard/note → opens the quick Bitácoras popup.
 *   - Cart → shows the venta's detalles (purchased items).
 *   - Eye → opens the full M1-M4 detail dialog.
 */
export function MantenimientoTable({
  rows,
  loading,
  error,
  searchActive,
  onView,
  onViewDetalles,
  onViewBitacoras,
  onRetry,
}: {
  rows: MantenimientoRow[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onView: (row: MantenimientoRow) => void;
  onViewDetalles: (row: MantenimientoRow) => void;
  onViewBitacoras: (row: MantenimientoRow) => void;
  onRetry: () => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay ventas en estado post-pago.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Status</Th>
              <Th>N° Cotización</Th>
              <Th>F. Venta</Th>
              <Th>Vendedor</Th>
              <Th>Cliente</Th>
              <Th>Teléfono</Th>
              <Th className="text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={7} />
            ) : error ? (
              <RowStatus cols={7}>
                <span className="text-destructive">
                  No se pudo cargar la cartera.
                </span>
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Reintentar
                </Button>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={7}>
                <Wrench className="h-5 w-5 text-muted-foreground" aria-hidden />
                {emptyMessage}
              </RowStatus>
            ) : (
              rows.map((r) => (
                <MantenimientoRowView
                  key={r.id}
                  row={r}
                  onView={() => onView(r)}
                  onViewDetalles={() => onViewDetalles(r)}
                  onViewBitacoras={() => onViewBitacoras(r)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
            <span>No se pudo cargar la cartera.</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Reintentar
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
            <Wrench className="h-5 w-5" aria-hidden />
            {emptyMessage}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <MantenimientoCard
                key={r.id}
                row={r}
                onView={() => onView(r)}
                onViewDetalles={() => onViewDetalles(r)}
                onViewBitacoras={() => onViewBitacoras(r)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MantenimientoCard({
  row,
  onView,
  onViewDetalles,
  onViewBitacoras,
}: {
  row: MantenimientoRow;
  onView: () => void;
  onViewDetalles: () => void;
  onViewBitacoras: () => void;
}) {
  const f = row.fields;
  const saldos = useSaldosEcheq();
  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <span className="truncate text-sm font-medium text-foreground">
          {f.Cliente_RO || '—'}
        </span>
        <EstadoConSaldoEcheq saldo={saldoEnDespacho(saldos, row.id, f.Status_RO)} sinMontos>
          <ShortStatusBadge status={f.Status_RO ?? ''} />
        </EstadoConSaldoEcheq>
      </div>
      <div className="text-xs text-muted-foreground">
        Cot. <span className="text-foreground">{row.id}</span> ·{' '}
        {f.FechaPago_RO || f.Fecha_RO || '—'}
        {f.Vendedor_RO && <> · {f.Vendedor_RO}</>}
      </div>
      {(row.telefono || row.correo) && (
        <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
          {row.telefono && <span>{row.telefono}</span>}
          {row.correo && <span className="truncate">{row.correo}</span>}
        </div>
      )}
      <div className="flex items-center justify-end gap-1.5">
        <IconBtn
          onClick={onViewBitacoras}
          label="Bitácoras"
          icon={<ClipboardList className="h-4 w-4" aria-hidden />}
        />
        <IconBtn
          onClick={onViewDetalles}
          label="Ver detalle de venta"
          icon={<ShoppingCart className="h-4 w-4" aria-hidden />}
        />
        <IconBtn
          onClick={onView}
          label="Ver mantenimiento"
          icon={<Eye className="h-4 w-4" aria-hidden />}
        />
      </div>
    </li>
  );
}

function MantenimientoRowView({
  row,
  onView,
  onViewDetalles,
  onViewBitacoras,
}: {
  row: MantenimientoRow;
  onView: () => void;
  onViewDetalles: () => void;
  onViewBitacoras: () => void;
}) {
  const f = row.fields;
  const saldos = useSaldosEcheq();
  return (
    <tr className="transition-colors hover:bg-muted/30">
      <Td>
        <EstadoConSaldoEcheq saldo={saldoEnDespacho(saldos, row.id, f.Status_RO)} sinMontos>
          <ShortStatusBadge status={f.Status_RO ?? ''} />
        </EstadoConSaldoEcheq>
      </Td>
      <Td className="font-medium text-foreground">{row.id}</Td>
      <Td className="text-muted-foreground">
        {f.FechaPago_RO || f.Fecha_RO || '—'}
      </Td>
      <Td>{f.Vendedor_RO || '—'}</Td>
      <Td>
        <div className="flex flex-col leading-tight">
          <span className="font-medium text-foreground">{f.Cliente_RO || '—'}</span>
          {row.correo ? (
            <span className="break-all text-xs text-muted-foreground">
              {row.correo}
            </span>
          ) : null}
        </div>
      </Td>
      <Td className="text-muted-foreground">{row.telefono || '-'}</Td>
      <Td>
        <div className="flex items-center justify-end gap-1.5">
          <IconBtn
            onClick={onViewBitacoras}
            label="Bitácoras"
            icon={<ClipboardList className="h-4 w-4" aria-hidden />}
          />
          <IconBtn
            onClick={onViewDetalles}
            label="Ver detalle de venta"
            icon={<ShoppingCart className="h-4 w-4" aria-hidden />}
          />
          <IconBtn
            onClick={onView}
            label="Ver mantenimiento"
            icon={<Eye className="h-4 w-4" aria-hidden />}
          />
        </div>
      </Td>
    </tr>
  );
}

function IconBtn({
  onClick,
  label,
  icon,
}: {
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
    >
      {icon}
    </button>
  );
}

/**
 * Compact status pill that matches the PowerApps label style:
 * uppercase, abbreviated phrasing, short-word color scheme.
 */
function ShortStatusBadge({ status }: { status: string }) {
  const { label, cls } = statusDisplay(status);
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wide',
        cls,
      )}
      title={status || '—'}
    >
      {label}
    </span>
  );
}

function statusDisplay(status: string): { label: string; cls: string } {
  switch (status) {
    case 'Pago Realizado':
      return { label: 'PAGO REALIZADO', cls: 'bg-emerald-100 text-emerald-700' };
    case 'Pendiente Despacho':
      return { label: 'PEND. DESPACHO', cls: 'bg-amber-100 text-amber-700' };
    case 'Despachado':
      return { label: 'DESPACHADO', cls: 'bg-sky-100 text-sky-700' };
    case 'Pendiente Puesta Marcha':
      return { label: 'PEND. P. EN MARCHA', cls: 'bg-indigo-100 text-indigo-700' };
    case 'Cerrado':
      return { label: 'CERRADO', cls: 'bg-slate-200 text-slate-600' };
    default:
      return { label: status || '—', cls: 'bg-slate-100 text-slate-600' };
  }
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-3 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-3 align-top', className)}>{children}</td>;
}

function RowStatus({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-3 py-3.5">
              <Skeleton className="h-4 w-full max-w-[120px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
